import IconsEnum from "../../../../classes/enums/IconsEnum";
import Button from "../../../../components/button/Button";
import { iconMapping } from "../../../../config/iconConfig";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import "./DragDropArea.css";

// Recursively collect all File objects from a FileSystemEntry, including nested
// subdirectories. readEntries() is capped at 100 results per call so we loop
// until it returns an empty batch.
const getAllFilesFromEntry = entry =>
  new Promise(resolve => {
    if (entry.isFile) {
      entry.file(
        file => resolve([file]),
        () => resolve([])
      );
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const allEntries = [];
      const readBatch = () => {
        reader.readEntries(
          async entries => {
            if (entries.length === 0) {
              const fileArrays = await Promise.all(
                allEntries.map(getAllFilesFromEntry)
              );
              resolve(fileArrays.flat());
            } else {
              allEntries.push(...entries);
              readBatch();
            }
          },
          () => resolve([])
        );
      };
      readBatch();
    } else {
      resolve([]);
    }
  });

const DragDropArea = ({ onAddFiles, allowedFileTypes, isImporting }) => {
  const { t } = useTranslation();

  const IconComponent = iconMapping[IconsEnum.UPLOAD];

  const handleDrop = async e => {
    e.preventDefault();
    const items = Array.from(e.dataTransfer.items);
    // Use FileSystemEntry API to recursively traverse dropped directories.
    // Falls back to getAsFile() for browsers that don't support webkitGetAsEntry.
    const allFiles = (
      await Promise.all(
        items.map(item => {
          const entry = item.webkitGetAsEntry?.();
          if (entry) return getAllFilesFromEntry(entry);
          const file = item.getAsFile?.();
          return file ? [file] : [];
        })
      )
    ).flat();
    if (allFiles.length > 0) onAddFiles(allFiles);
  };

  const handleFileInput = e => {
    const files = Array.from(e.target.files);
    onAddFiles(files);

    setTimeout(() => {
      e.target.value = "";
    }, 100);
  };

  return (
    <div
      className={`drag-drop-area ${isImporting ? "disabled" : ""}`}
      onDrop={!isImporting ? handleDrop : undefined}
      onDragOver={e => !isImporting && e.preventDefault()}
    >
      <div className="drag-drop-message-container">
        {IconComponent && <IconComponent />}
        <div>{t("105")}</div>
      </div>

      <div className="App-text-small">{t("107")}</div>
      <input
        type="file"
        id="fileInput"
        multiple
        accept={allowedFileTypes.join(",")}
        onChange={handleFileInput}
        style={{ display: "none" }}
      />
      <input
        type="file"
        id="directoryInput"
        webkitdirectory="true"
        directory="true"
        onChange={handleFileInput}
        style={{ display: "none" }}
      />
      <div className="browse-buttons-container">
        <Button
          value={t("108")}
          onClick={() => document.getElementById("fileInput").click()}
          disabled={isImporting}
        />
        <Button
          value={t("112")}
          onClick={() => document.getElementById("directoryInput").click()}
          disabled={isImporting}
        />
      </div>

      <div className="drag-drop-text-info-wrapper">
        <div className="drag-drop-text-info-bold App-text-medium">
          {t("106") + ":"}
        </div>
        <div className="drag-drop-text-info App-text-small">
          {allowedFileTypes.join(", ")}
        </div>
      </div>
    </div>
  );
};

export default DragDropArea;
